import { Delegate } from '@theme/delegate';

/**
 * Search results page.
 *
 * Products and articles are rendered as two grids from the same result set; the tabs
 * toggle between them client-side (no fetch, no redirect). Counts are exact from Liquid
 * (the section counts the full result set with a large paginate), so nothing is fetched.
 * "Load more" reveals product cells that start hidden past the per-page limit — every
 * result is already in the DOM, so counts and the grid can never desync.
 */
class SearchComponent extends HTMLElement {
  #delegate = null;
  #boundSync = null;
  #originalArticleCount = null;
  #originalProductCount = null;
  #hadMixedResults = false;
  #hasActiveFilters = false;
  #hydrateController = null;

  connectedCallback() {
    if (this.#delegate) {
      return;
    }

    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-search-type-link]', this.#onTypeLinkClick.bind(this));
    this.#delegate.on('click', '[data-action="reveal-more"]', this.#onRevealMore.bind(this));

    // Product filters strip articles from the mixed result, so a filtered re-render
    // reports 0 articles. During client-side filtering the article grid is preserved,
    // but a hard refresh on a filtered URL re-renders it away and would hide the tabs.
    const mainGrid = document.getElementById('mainGrid');
    this.#originalProductCount = this.#parseCount(mainGrid?.dataset.productCount) ?? 0;
    this.#originalArticleCount = this.#parseCount(mainGrid?.dataset.articleCount) ?? 0;
    this.#hasActiveFilters = this.#urlHasActiveFilters();
    // With filters active, assume the query is mixed (it still produced products) so the
    // tabs show immediately; #hydrateArticlesUnderFilter then fetches the true article
    // count from the filter-free URL and confirms or collapses it.
    this.#hadMixedResults = this.#hasActiveFilters
      ? this.#originalProductCount > 0
      : this.#originalProductCount > 0 && this.#originalArticleCount > 0;

    // Filters re-render the section synchronously and swap toolbar/type-bar nodes
    // after holder-replaced; sync again once those swaps finish so client-side tab
    // visibility wins over the server-filtered markup (which hides the type bar).
    this.#boundSync = () => queueMicrotask(() => this.#sync());
    document.addEventListener('collection-grid:holder-replaced', this.#boundSync);
    document.addEventListener('collection-grid:toolbar-replaced', this.#boundSync);
    document.addEventListener('search:type-synced', this.#boundSync);

    this.#sync();
    this.#restorePersistedType();
    this.#hydrateArticlesUnderFilter();

    // The pre-paint guard (main-search.liquid) may have forced the article view via a CSS
    // flag to avoid a product-grid flash on reload. The real attribute swap has now run, so
    // drop the flag — otherwise a later click back to Products would stay CSS-hidden.
    document.documentElement.removeAttribute('data-search-force-type');
  }

  disconnectedCallback() {
    this.#delegate?.off();
    this.#delegate = null;

    this.#hydrateController?.abort();
    this.#hydrateController = null;

    if (this.#boundSync) {
      document.removeEventListener('collection-grid:holder-replaced', this.#boundSync);
      document.removeEventListener('collection-grid:toolbar-replaced', this.#boundSync);
      document.removeEventListener('search:type-synced', this.#boundSync);
      this.#boundSync = null;
    }
  }

  #urlHasActiveFilters() {
    const params = new URLSearchParams(window.location.search);
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) {
        return true;
      }
    }
    return false;
  }

  // A product filter strips articles from the mixed search, so on a filtered URL the
  // article grid and count arrive empty. Re-fetch them from the filter-free article
  // URL (the Journal tab's own href) and swap them in — the approach used on
  // wristbuddy — so the Journal tab keeps its real content and count across a refresh.
  async #hydrateArticlesUnderFilter() {
    // Nothing to recover when unfiltered (counts already authoritative) or when the
    // filtered result already carried articles.
    if (!this.#hasActiveFilters || this.#originalArticleCount > 0) {
      return;
    }

    const articleUrl = this.querySelector(
      '[data-search-type-link][data-search-type="article"]'
    )?.href;
    if (!articleUrl) {
      return;
    }

    this.#hydrateController?.abort();
    this.#hydrateController = new AbortController();

    let doc;
    try {
      const html = await fetch(articleUrl, { signal: this.#hydrateController.signal }).then(
        (res) => res.text()
      );
      doc = new DOMParser().parseFromString(html, 'text/html');
    } catch {
      return;
    }

    if (!this.isConnected) {
      return;
    }

    // Swap in the unfiltered article grid so switching to Journal shows real results.
    const freshGrid = doc.querySelector('[data-search-grid="article"]');
    const currentGrid = this.querySelector('[data-search-grid="article"]');
    if (freshGrid && currentGrid) {
      currentGrid.innerHTML = freshGrid.innerHTML;
    }

    // Only decide from a valid article response; on any other shape keep the
    // optimistic tabs (never hide them on an inconclusive fetch).
    if (!freshGrid) {
      return;
    }

    // Confirm or collapse the optimistic tabs from the fetched grid's REAL article
    // cells — NOT data-article-count. That count can come back 0 for a wildcard
    // query (e.g. "a*") even when articles exist, and trusting it re-hid the whole
    // type bar on a filtered reload of a mixed search (the desktop tab list then
    // "disappeared"). The rendered cells are the DOM truth (all articles are
    // emitted; only those past the per-page limit are [hidden]).
    const articleCells = freshGrid.querySelectorAll('[data-search-article-cell]').length;
    const fetchedCount = this.#parseCount(doc.getElementById('mainGrid')?.dataset.articleCount);
    this.#originalArticleCount = fetchedCount && fetchedCount > 0 ? fetchedCount : articleCells;
    this.#hadMixedResults = this.#originalProductCount > 0 && articleCells > 0;
    this.#sync();
  }

  #sync() {
    const mainGrid = document.getElementById('mainGrid');

    this.#hasActiveFilters = this.#urlHasActiveFilters();

    // Product count follows filters; article count is frozen at its unfiltered value.
    const productCount = this.#parseCount(mainGrid?.dataset.productCount);
    if (productCount !== null) {
      this.dataset.productCount = String(productCount);
    }

    if (!this.#hasActiveFilters) {
      const serverArticles = this.#parseCount(mainGrid?.dataset.articleCount) ?? 0;
      const articleCells = this.querySelectorAll('[data-search-article-cell]').length;
      if (serverArticles > 0) {
        this.#originalArticleCount = serverArticles;
      } else if (articleCells > 0) {
        this.#originalArticleCount = articleCells;
      }
      const products = productCount ?? this.#originalProductCount ?? 0;
      const articles = Math.max(this.#originalArticleCount ?? 0, serverArticles, articleCells);
      this.#hadMixedResults = products > 0 && articles > 0;
    }

    this.dataset.articleCount = String(
      this.#originalArticleCount ?? this.#parseCount(mainGrid?.dataset.articleCount) ?? 0
    );

    const type = mainGrid?.dataset.searchResultType || this.dataset.searchType || 'product';
    this.dataset.searchType = type;

    this.#renderCounts();
    this.#applyTypeBarVisibility();
    this.#syncActiveTypeLinks();
    this.#updateToolbarCounter(type);
    this.#syncLoadMore(type);
    this.#syncColumnSwitcherVisibility();
  }

  #syncColumnSwitcherVisibility() {
    if (this.dataset.enableColumnSwitcher !== 'false') {
      return;
    }

    this.querySelectorAll('[data-column-switcher]').forEach((switcher) => {
      switcher.remove();
    });
  }

  // Re-derive each "load more" wrapper from its grid after a filter/reset or tab
  // change: align its counter and visibility with the client-side reveal/tab state.
  // Each wrapper shows only when its type is active and hidden items remain.
  #syncLoadMore(type) {
    // Product and article grids each get their own load-more; show the one whose
    // type is active and still has hidden items, and keep its counter in sync.
    this.querySelectorAll('[data-search-load-more]').forEach((wrapper) => {
      const wrapperType = wrapper.dataset.loadMoreType || 'product';
      const grid = document.querySelector(wrapper.dataset.grid || '#mainCollectionHolder');
      const itemSelector = wrapper.dataset.item || '[data-product-cell]';
      if (!grid) {
        return;
      }

      const total = grid.querySelectorAll(itemSelector).length;
      const remaining = grid.querySelectorAll(`${itemSelector}[hidden]`).length;
      const shown = total - remaining;

      const counter = wrapper.querySelector('[data-search-load-more-counter]');
      if (counter) {
        counter.textContent = this.#formatCounter(wrapper, shown, total);
      }

      wrapper.toggleAttribute('hidden', type !== wrapperType || remaining === 0);
    });
  }

  #onTypeLinkClick(event) {
    const link = event.target.closest('[data-search-type-link]');
    if (!link) {
      return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (link.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.#activateType(link.dataset.searchType || 'product');
  }

  #activateType(type) {
    if (this.dataset.searchType === type) {
      return;
    }
    this.dataset.searchType = type;

    const mainGrid = document.getElementById('mainGrid');
    if (mainGrid) {
      mainGrid.dataset.searchResultType = type;
    }

    this.querySelectorAll('[data-search-grid]').forEach((grid) => {
      grid.toggleAttribute('hidden', grid.dataset.searchGrid !== type);
    });

    // Reveal the load-more belonging to the now-active type; the filter/sort/column
    // controls stay product-only.
    this.#syncLoadMore(type);
    this.#toggleProductToolbar(type === 'product');

    this.#syncActiveTypeLinks();
    this.#updateToolbarCounter(type);
    this.#persistType(type);
  }

  // Tabs switch client-side without touching the URL, so a refresh would re-render
  // the server default (product) and lose the visitor's tab. Persist the choice per
  // query and restore it on load — unless the URL pins a type explicitly.
  #storageKey() {
    return `almada:search-type:${this.dataset.searchTerms || ''}`;
  }

  #persistType(type) {
    try {
      sessionStorage.setItem(this.#storageKey(), type);
    } catch {
      // sessionStorage can throw (private mode / quota) — persistence is a nicety.
    }
  }

  #restorePersistedType() {
    // An explicit type in the URL wins; only restore for a mixed search (both tabs shown).
    if (new URLSearchParams(window.location.search).has('type') || !this.#hadMixedResults) {
      return;
    }
    let stored = null;
    try {
      stored = sessionStorage.getItem(this.#storageKey());
    } catch {
      return;
    }
    if (stored && stored !== this.dataset.searchType) {
      this.#activateType(stored);
    }
  }

  #toggleProductToolbar(showProduct) {
    document
      .querySelectorAll('[data-collection-toolbar-replace] .collection-grid__toolbar-end')
      .forEach((el) => el.toggleAttribute('hidden', !showProduct));
  }

  #onRevealMore(event) {
    const wrapper = event.target.closest('[data-search-load-more]');
    if (!wrapper) {
      return;
    }
    event.preventDefault();

    const grid = document.querySelector(wrapper.dataset.grid || '#mainCollectionHolder');
    const itemSelector = wrapper.dataset.item || '[data-product-cell]';
    const perPage = parseInt(wrapper.dataset.perPage || '', 10) || 8;
    if (!grid) {
      return;
    }

    // Reveal the next batch in load order (data-load-order) so the two article
    // columns fill evenly; product cells have no order attr and keep DOM order.
    Array.from(grid.querySelectorAll(`${itemSelector}[hidden]`))
      .sort((a, b) => (Number(a.dataset.loadOrder) || 0) - (Number(b.dataset.loadOrder) || 0))
      .slice(0, perPage)
      .forEach((cell) => {
        cell.removeAttribute('hidden');
        cell.removeAttribute('data-load-hidden');
      });

    const total = grid.querySelectorAll(itemSelector).length;
    const remaining = grid.querySelectorAll(`${itemSelector}[hidden]`).length;
    const shown = total - remaining;

    // The header/toolbar results count reflects the active type, and a reveal only
    // fires on the active tab's button — so it always matches the revealed grid.
    this.#writeToolbarCount(shown, total);

    const counter = wrapper.querySelector('[data-search-load-more-counter]');
    if (counter) {
      counter.textContent = this.#formatCounter(wrapper, shown, total);
    }

    if (remaining === 0) {
      wrapper.setAttribute('hidden', '');
    }
  }

  #formatCounter(wrapper, shown, total) {
    const template = wrapper.dataset.counterTemplate;
    if (template) {
      return template.replaceAll('__COUNT__', String(shown)).replaceAll('__TOTAL__', String(total));
    }
    return `${shown} / ${total}`;
  }

  #updateToolbarCounter(type) {
    const products = this.#parseCount(this.dataset.productCount) ?? 0;
    const articles = this.#parseCount(this.dataset.articleCount) ?? 0;

    if (type === 'article') {
      this.#writeToolbarCount(this.#visibleArticleCount(), articles);
    } else {
      this.#writeToolbarCount(this.#visibleProductCount(), products);
    }
  }

  #visibleProductCount() {
    return this.#visibleCount('#mainCollectionHolder', '[data-product-cell]');
  }

  #visibleArticleCount() {
    return this.#visibleCount('#mainArticlesHolder', '[data-search-article-cell]');
  }

  #visibleCount(gridSelector, itemSelector) {
    const grid = document.querySelector(gridSelector);
    if (!grid) {
      return 0;
    }
    const all = grid.querySelectorAll(itemSelector).length;
    const hidden = grid.querySelectorAll(`${itemSelector}[hidden]`).length;
    return all - hidden;
  }

  #writeToolbarCount(shown, total) {
    document.querySelectorAll('[data-toolbar-products-shown]').forEach((el) => {
      el.textContent = String(shown);
    });

    const articleTotals = document.querySelectorAll('[data-toolbar-articles-total]');
    if (articleTotals.length === 0) {
      document.querySelectorAll('[data-toolbar-products-total]').forEach((el) => {
        el.textContent = String(total);
      });
      return;
    }

    const type = this.dataset.searchType || 'product';
    document.querySelectorAll('[data-toolbar-products-total]').forEach((el) => {
      if (type === 'product') {
        el.textContent = String(total);
      }
      el.toggleAttribute('hidden', type !== 'product');
    });
    articleTotals.forEach((el) => {
      if (type === 'article') {
        el.textContent = String(total);
      }
      el.toggleAttribute('hidden', type !== 'article');
    });
  }

  #syncActiveTypeLinks() {
    const currentType = this.dataset.searchType || 'product';
    this.querySelectorAll('[data-search-type-link]').forEach((link) => {
      link.classList.toggle('is-active', link.dataset.searchType === currentType);
    });
  }

  #renderCounts() {
    const products = this.#parseCount(this.dataset.productCount) ?? 0;
    const articles = this.#parseCount(this.dataset.articleCount) ?? 0;
    this.#writeCount('product', products);
    this.#writeCount('article', articles);
  }

  #applyTypeBarVisibility() {
    this.querySelectorAll('[data-search-type-bar]').forEach((bar) => {
      bar.toggleAttribute('hidden', !this.#hadMixedResults);
    });
  }

  #writeCount(type, count) {
    const countSelector =
      type === 'article' ? '[data-search-article-count]' : '[data-search-product-count]';

    this.querySelectorAll(countSelector).forEach((el) => {
      el.textContent = `(${count})`;
    });

    this.querySelectorAll(`[data-search-type-link][data-search-type="${type}"]`).forEach((link) => {
      if (count > 0) {
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      } else {
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
    });
  }

  #parseCount(value) {
    const count = parseInt(value ?? '', 10);
    return Number.isFinite(count) ? count : null;
  }
}

customElements.define('search-component', SearchComponent);
